/**
 * Soft!=Live Page195 Soft residual — Settings shell residual liquid (account_nav || ops_nav).
 * Mount: /settings/*
 * Buses (contract ops — never painted in operator DOM):
 *   get__settings_status (always-on posture · dual account || ops)
 * Child panes keep their own Live status SoTs.
 * Soft residual deleted:
 *  - Soft all-rail invent without posture seats
 *  - Soft OTA/AI/admin desks for user floor without Live seat
 *  - Soft fabric for non-SA seats
 *  - Soft denial theatre without posture KPIs
 *  - Soft client me-rail invent while loading / partial
 *  - path /operation labels painted in DOM
 */
import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, LayoutGroup } from "framer-motion";
import { html, M, Ico, Alert, SPRING, SOFT, go, hashSearchParams } from "./ui.js";
import * as D from "./data.js";
import { path, useApi } from "./api.js";
import { useSession } from "./session.js";
import { confirmLeaveSeat } from "./shell-wayfind.js";
import { AuditExportDesk } from "./audit-export-desk.js";
import { DelegationsDesk } from "./delegations-desk.js";
import { BillingDesk } from "./billing-desk.js";
import { NotifySettingsDesk } from "./notify-settings-desk.js";
import { AlarmBehaviorDesk, TriggerPolicyDesk } from "./alarm-settings-desk.js";
import AiAdminWorkbench from "./ai-admin-workbench.js";
import { IntegrateSettingsDesk } from "./integrate-settings-desk.js";
import { DangerSettingsDesk } from "./danger-settings-desk.js";
import { PasswordSettingsDesk } from "./password-settings-desk.js";
import { SessionsSettingsDesk } from "./sessions-settings-desk.js";
import { ProfileSettingsDesk } from "./profile-settings-desk.js";
import { UsersSettingsDesk } from "./users-settings-desk.js";

const GROUP_MARK = Object.freeze({
  "Account & Access": "You",
  "Notifications & Alarms": "Alerts",
  "AI Control": "Team",
  "Integrations": "Links",
  "Governance & Risk": "Risk",
});

const PANE_FLAG = {
  profile: "profilePaneAllowed",
  security: "securityPaneAllowed",
  sessions: "sessionsPaneAllowed",
  delegations: "delegationsPaneAllowed",
  users: "usersPaneAllowed",
  notify: "notifyPaneAllowed",
  alarm: "alarmPaneAllowed",
  trigger: "triggerPaneAllowed",
  models: "modelsPaneAllowed",
  fabric: "fabricPaneAllowed",
  ai: "aiPaneAllowed",
  cameras: "camerasPaneAllowed",
  sites: "sitesPaneAllowed",
  integrate: "integratePaneAllowed",
  audit: "auditPaneAllowed",
  billing: "billingPaneAllowed",
  ota: "otaPaneAllowed",
  danger: "dangerPaneAllowed",
};

/** Path-free operator copy for Settings shell. sealed panels forbidden. */
export function softSettingsShellErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|seat_gated|not_authorized|denied/.test(low)) {
    return "Your account cannot open this Settings seat. Ask a manager if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found/.test(low)) {
    return "That Settings seat is not available.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable/.test(low)) {
    return "Settings is not ready right now.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/api\/|\/settings\/|GET |POST |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}

/** Soft!=Live Page195: missing contract ops must not crash the shell. */
function safeSettingsPath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__settings_status") return "/settings/status";
    return "";
  }
}

/** Pure: seat chips from Live Settings shell posture (Page195) — fail-closed. */
export function settingsShellSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const panesOpen = Number(lim.panes_open);
  return {
    accountNavAllowed: p.account_nav_allowed === true,
    opsNavAllowed: p.ops_nav_allowed === true,
    signOutAllowed: p.sign_out_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    profilePaneAllowed: p.profile_pane_allowed === true,
    securityPaneAllowed: p.security_pane_allowed === true,
    sessionsPaneAllowed: p.sessions_pane_allowed === true,
    delegationsPaneAllowed: p.delegations_pane_allowed === true,
    usersPaneAllowed: p.users_pane_allowed === true,
    notifyPaneAllowed: p.notify_pane_allowed === true,
    alarmPaneAllowed: p.alarm_pane_allowed === true,
    triggerPaneAllowed: p.trigger_pane_allowed === true,
    modelsPaneAllowed: p.models_pane_allowed === true,
    fabricPaneAllowed: p.fabric_pane_allowed === true,
    aiPaneAllowed: p.ai_pane_allowed === true,
    camerasPaneAllowed: p.cameras_pane_allowed === true,
    sitesPaneAllowed: p.sites_pane_allowed === true,
    integratePaneAllowed: p.integrate_pane_allowed === true,
    auditPaneAllowed: p.audit_pane_allowed === true,
    billingPaneAllowed: p.billing_pane_allowed === true,
    otaPaneAllowed: p.ota_pane_allowed === true,
    dangerPaneAllowed: p.danger_pane_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    postureLabel: String(p.posture_label || "").trim(),
    defaultPane: String(p.default_pane || "profile"),
    panesOpen: Number.isFinite(panesOpen) && panesOpen >= 0 ? Math.floor(panesOpen) : 0,
  };
}

/** Pure: Live rail intersect catalog — Soft invent sealed panels forbidden. */
export function settingsShellRailOf(seat, catalog) {
  const cat = Array.isArray(catalog) ? catalog : [];
  const s = seat || {};
  return cat.filter((item) => {
    if (!item || !item.id) return false;
    const flag = PANE_FLAG[item.id];
    if (!flag) return false;
    return s[flag] === true;
  });
}

export default function Settings({ sub, toast }) {
  const { me } = useSession();
  const posture = useApi(safeSettingsPath("get__settings_status"));
  const post = posture.data || {};
  // Fail-closed while loading/empty — no Soft rail invent.
  const seat = settingsShellSeatOf(posture.loading || !posture.data ? {} : post);

  /* Live status seats only — Soft me invent of sealed panes forbidden. */
  const rail = useMemo(
    () => settingsShellRailOf(seat, D.SETTINGS_NAV),
    [seat, posture.loading, posture.data],
  );

  const postureReady = !posture.loading && !!posture.data;
  const knownSub = D.SETTINGS_NAV.some((s) => s.id === sub);
  /* Fail-closed rail is empty while posture loads. Do not rewrite #/settings/ai
     (or ?lane=) to Profile — that is the deep-link refresh defect. */
  const resolvedAt = rail.some((s) => s.id === sub)
    ? sub
    : (!postureReady && knownSub
      ? sub
      : (rail.some((s) => s.id === seat.defaultPane) ? seat.defaultPane : (rail[0] && rail[0].id) || "profile"));
  /* Prefer URL `sub` while posture loads or when allowed — never paint Profile
     over a known deep-link before the seat answers. */
  const paintAt = (!postureReady && knownSub)
    ? sub
    : (rail.some((s) => s.id === sub) ? sub : resolvedAt);
  const [at, setAt] = useState(paintAt);
  useEffect(() => setAt(paintAt), [paintAt]);
  const settingsHref = (id) => {
    if (id !== "ai") return "/settings/" + id;
    const lane = String(hashSearchParams().get("lane") || "").trim();
    return lane ? ("/settings/ai?lane=" + encodeURIComponent(lane)) : "/settings/ai";
  };
  /* Wave 0: use go() not stay(). stay() replaceState skips hashchange, so the
     address bar can show Profile while the pane (local `at`) still paints AI —
     the deep-link / refresh / Back defect in the UI audit. */
  const select = (id) => {
    setAt(id);
    go(settingsHref(id));
  };
  /* Integrations is a catalog group, not a city. Telegram & voice paint under Alerts. */
  const paintGroupOf = (g) => (g === "Integrations" ? "Notifications & Alarms" : g);
  const groupSeen = new Set(rail.map((s) => paintGroupOf(s.group)));
  const groups = [
    ...D.SETTINGS_L2_GROUPS.filter((g) => g !== "Integrations" && groupSeen.has(g)),
    ...[...groupSeen].filter((g) => !D.SETTINGS_L2_GROUPS.includes(g)),
  ];
  const denied = postureReady && knownSub && !rail.some((s) => s.id === sub);
  useEffect(() => {
    /* Only bounce after Live posture proves the pane is sealed — never on load. */
    if (!postureReady || !denied) return;
    if (resolvedAt && resolvedAt !== sub) go(settingsHref(resolvedAt));
  }, [postureReady, denied, resolvedAt, sub]);
  const Pane = PANES[at] || (knownSub && PANES[sub]) || PANES.profile;
  const atItem = rail.find((s) => s.id === at) || null;
  const roleLabel = seat.role || (me && me.role) || "—";
  const postureLabel = seat.postureLabel
    || (seat.accountNavAllowed
      ? (seat.opsNavAllowed
        ? (seat.otaPaneAllowed ? "Settings open · updates seat open" : "Settings open · operator seats")
        : "Account open · operations sealed")
      : (seat.opsNavAllowed ? "Account sealed · operations may stay open" : "Settings fully sealed"));

  const reRead = () => {
    posture.reload && posture.reload();
    toast && toast("Settings status refreshed", "go");
  };

  const PANEL_MOTION = SOFT;
  const NAV_PILL = SPRING;
  const groupMark = (g) => GROUP_MARK[g] || g;

  if (posture.loading && !posture.data) {
    return html`
      <div className="sheet set-shell set-shell--liquid desk-craft set-shell--v1084 set-shell--v1085 house-rack"
        data-cmc-page="settings-shell" data-cmc-residual="settings-shell"
        data-set-craft="v450">
        <div className="wrap set-shell__in">
          <p className="note">Confirming Settings seat…</p>
        </div>
      </div>`;
  }

  if (!posture.loading && !seat.accountNavAllowed && !seat.opsNavAllowed) {
    return html`
      <div className="sheet set-shell set-shell--liquid desk-craft set-shell--v1084 set-shell--v1085 house-rack"
        data-cmc-page="settings-shell" data-cmc-residual="settings-shell"
        data-set-craft="v450">
        <div className="wrap set-shell__in">
          <${Alert} tone="bad" icon="ban" title="Settings shell sealed"
            say="Your account cannot open personal or operations settings yet."/>
          <p className="note">${postureLabel}</p>
          <button type="button" className="btn btn--ghost btn--sm"
            disabled=${!seat.reReadAllowed || posture.loading} onClick=${reRead}>
            <${Ico} d="refresh" w=${14}/> Re-read
          </button>
        </div>
      </div>`;
  }

  return html`
    <div className="sheet set-shell set-shell--liquid desk-craft set-shell--v450 set-shell--v1084 set-shell--v1085 house-rack"
      data-cmc-page="settings-shell" data-cmc-residual="settings-shell"
      data-set-craft="v450"
      data-cmc-shell="settings-ux">
      <div className="wrap set-shell__in">
        <${M.header}
          className="set-shell__head set-shell__head--kinetic"
          initial=${{ opacity: 0, y: 10 }}
          animate=${{ opacity: 1, y: 0 }}
          transition=${SOFT}>
          <i className="set-shell__glow" aria-hidden="true"/>
          <div className="set-shell__copy">
            <div className="set-shell__titleline">
              <span className="set-shell__mark">Settings</span>
              <h1 className="ph set-shell__title">Settings</h1>
              <span className="set-shell__mode" data-live="1">
                <i className="set-shell__mode-dot" aria-hidden="true"/> Site
              </span>
            </div>
            <p className="psub set-shell__lede">
              ${atItem
                ? html`<b>${atItem.label}</b><span> · ${paintGroupOf(atItem.group)}</span>`
                : html`Seat, alerts, and AI studio.`}
            </p>
            <p className="set-shell__meters set-shell__meters--line" role="group" aria-label="Settings shell posture">
              ${[
                seat.accountNavAllowed ? { k: "Account", v: "open", on: true } : null,
                seat.opsNavAllowed ? { k: "Ops", v: "open", on: true } : null,
                seat.otaPaneAllowed ? { k: "Updates", v: "open", on: true } : null,
                { k: "Seat", v: roleLabel, on: !!(seat.role || (me && me.role)) },
              ].filter(Boolean).map((m) => html`<span key=${m.k} data-on=${m.on ? "1" : "0"}>${m.k} ${m.v}</span>`)}
            </p>
          </div>
        <//>

        ${posture.error ? html`
          <${Alert} tone="warn" title="Could not refresh Settings status"
            say=${softSettingsShellErrorOf(posture.error)}/>` : null}

        <div className="set-shell__work">
          <nav className="set-shell__index" aria-label="Settings panes">
            <i className="set-shell__bay" aria-hidden="true"/>
            <${LayoutGroup} id="set-shell-nav">
              ${groups.map((g) => html`
                <div key=${g} className="set-shell__block" role="tablist" aria-label=${g}>
                  <span className="set-shell__block-k"><em>${groupMark(g)}</em>${g}</span>
                  <div className="set-shell__block-list">
                    ${rail.filter((s) => paintGroupOf(s.group) === g).map((s) => html`
                      <button key=${s.id} type="button" role="tab"
                        className="set-shell__tab"
                        data-on=${at === s.id ? "1" : "0"}
                        aria-selected=${at === s.id ? "true" : "false"}
                        onClick=${() => select(s.id)}>
                        ${at === s.id
                          ? html`<${M.span}
                              layoutId="set-nav-pill"
                              className="set-shell__tab-pill"
                              transition=${NAV_PILL}
                              aria-hidden="true"/>`
                          : null}
                        <i className="set-shell__tick" aria-hidden="true"/>
                        <span className="set-shell__tab-lab">${s.label}</span>
                      </button>`)}
                  </div>
                </div>`)}
            <//>
            <div className="btn-row set-shell__acts" role="toolbar" aria-label="Settings shell">
              <button type="button" className="btn btn--ghost btn--sm"
                disabled=${!seat.reReadAllowed || posture.loading}
                onClick=${reRead}>
                <${Ico} d="refresh" w=${14}/> Re-read
              </button>
              <button type="button" data-cmc-signout="settings"
                className="btn btn--ghost btn--sm set-shell__signout"
                disabled=${!seat.signOutAllowed}
                onClick=${() => go("/auth/signout")}>
                <${Ico} d="logout" w=${14}/> Sign out
              </button>
            </div>
            ${!rail.length ? html`<div className="house-rack__empty">
              <b>No Settings panes yet</b>
              <p>This account has no open Settings seats. Ask a manager if you need one.</p>
            </div>` : null}
          </nav>

          <section className="set-shell__stage" aria-label=${atItem ? atItem.label : "Settings pane"}>
            ${at === "ai" ? null : html`<header className="set-shell__stage-hd">
              <div>
                <span className="set-shell__stage-k">${(atItem && paintGroupOf(atItem.group)) || "Pane"}</span>
                <b className="set-shell__stage-title">${(atItem && atItem.label) || "Settings"}</b>
              </div>
            </header>`}
            <div className="set-shell__stage-body">
              <${AnimatePresence} mode="wait">
                <${M.div}
                  key=${at}
                  className="set-shell__pane"
                  initial=${{ opacity: 0, y: 10 }}
                  animate=${{ opacity: 1, y: 0 }}
                  exit=${{ opacity: 0, y: -6 }}
                  transition=${PANEL_MOTION}>
                  <${Pane} toast=${toast}/>
                <//>
              <//>
            </div>
          </section>
        </div>
      </div>
    </div>`;
}

const PANES = {
/* Soft!=Live Page63 — Profile Live desk (avatar upload + honesty flags). */
profile: ({ toast }) => html`<${ProfileSettingsDesk} toast=${toast}/>`,

/* Soft!=Live Page61 — Password Live desk. */
security: ({ toast }) => html`<${PasswordSettingsDesk} toast=${toast}/>`,

/* Soft!=Live Page62 — Sessions Live desk (version-bump honesty · list invent forbidden). */
sessions: ({ toast }) => html`<${SessionsSettingsDesk} toast=${toast}/>`,

/* ── Notifications — Page51 full Live Soft!=Live ──────────────────────── */
notify: ({ toast }) => html`<${NotifySettingsDesk} toast=${toast}/>`,

/* ── staff delegations (tip599 + Page46 Live mount) ─────────────────── */
delegations: ({ toast }) => html`<${DelegationsDesk} toast=${toast}/>`,
users: ({ toast }) => html`<${UsersSettingsDesk} toast=${toast}/>`,

/* Soft!=Live Page52 — group-card alarm + device trigger policy Live desks. */
alarm: ({ toast }) => html`<${AlarmBehaviorDesk} toast=${toast}/>`,
trigger: ({ toast }) => html`<${TriggerPolicyDesk} toast=${toast}/>`,

/* SETTINGS-V2: decisions, models, and credential posture share one AI workbench. */
ai: ({ toast }) => html`<${AiAdminWorkbench} toast=${toast}/>`,

/* ── Audit export — tip599 + Page46 Live mount (Soft demote deleted) ─── */
audit: ({ toast }) => html`<${AuditExportDesk} toast=${toast}/>`,

/* SET-1 / SETTINGS-UX: OTA primary owns updates — Settings keeps handoff only. */
ota: () => html`<div data-cmc-set="set-1-ota-handoff">
    <${Alert} tone="info" title="OTA moved"
      say="Board images live under OTA. Capability packs live under Skills. Settings hosts neither workspace."/>
    <div className="btn-row" style=${{ marginTop: 12 }}>
      <button type="button" className="btn btn--go btn--sm"
        onClick=${() => confirmLeaveSeat({ fromRoute: "/settings/ota", targetPath: "/ota" })}>
        Open OTA primary</button>
      <button type="button" className="btn btn--ghost btn--sm"
        onClick=${() => confirmLeaveSeat({ fromRoute: "/settings/ota", targetPath: "/machine" })}>
        Machine</button>
    </div>
  </div>`,

/* ── Billing — Page47 Live desk (Soft demote / flat invent deleted) ─── */
billing: ({ toast }) => html`<${BillingDesk} toast=${toast}/>`,

/* Soft!=Live Page60 — Integrate Live desk (Soft voice feature wall deleted). */
integrate: ({ toast }) => html`<${IntegrateSettingsDesk} toast=${toast}/>`,
/* Soft!=Live Page61 — Danger zone Live desk (sa blocked honesty). */
danger: ({ toast }) => html`<${DangerSettingsDesk} toast=${toast}/>`,
};
